import React, { useEffect, useLayoutEffect, useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { connect, useDispatch } from "react-redux";
import _ from "lodash";

import {
  Button,
  Confirm,
  Message,
  Modal,
  Header,
  Grid,
  GridColumn,
} from "semantic-ui-react";

import { useTranslation } from "react-i18next";

import { useTable, useSortBy, useFilters, usePagination, useGlobalFilter, useColumnOrder } from "react-table";
import { arrayMove } from "@dnd-kit/sortable";

import DataExportButton from "./DataExportButton/DataExportButton";
import ProgressIndicator from "./ProgressIndicator/ProgressIndicator";

import {
  getLayouts,
  saveLayout,
  selectLayout,
  deleteLayout,
  patchLayout,
  editLayout,
  setSelectedRow,
  commit,
  undoCommit,
  updateTableCellsAndSync,
  updateTableRow,
  updateTableCellByRowAndColumn,
  setNotificationFilter,
} from "../../actions/ordersTable";

import { setCurrentPage, setPageSize } from "../../actions/items";

import { useMemoCompare } from "../../utils/hooks";

import { itemAccessors } from "../../utils/constants";

import {
  mapAccessorsToColumns,
  getSuggestedOrderColumn,
  mapItemsToRecordsObject,
  getSortedColumns,
  formatDynamicFieldValues,
} from "../../utils/mappers/ordersTable";

import { getSafeSelectedWorkspace } from "../../utils/mappers/user";

import { resolveEditPermission } from "../../utils/mappers/workspace";

import { equalsAny, multipleOptions, includesPartial } from "../../utils/filters";

import Table from "./Table/Table";
import CustomLayoutModal from "./CustomLayoutModal/CustomLayoutModal";
import SuggestedOrderFilter from "./Filters/SuggestedOrderFilter";

import ActualOrderCell from "./EditableCells/ActualOrderCell";
import VarianceCell from "./EditableCells/VarianceCell";

import "./OrdersTable.css";
import PageSelector from "./PageSelector/PageSelector";
import SearchBar from "./SearchBar/SearchBar";
import OrdersErrorDropdown from "./OrderErrors/OrderErrors";
import OrderColumns from "./Ordercolumns/OrderColumns";
import DynamicFieldsButton from "./DynamicFieldsButton/DynamicFieldsButton";
import DynamicFieldsModal from "./DynamicFieldsModal/DynamicFieldsModal";
import {
  doLoadMetadata,
  doSaveDynamicField,
  doSingleCalcForTableCell,
  doUpdateDynamicField
} from "../../actions/dynamicFields";
import { parseFormulaForCalculation } from "../../utils/dynamicFormulaParser";
import { GET_FILTERS, GET_GLOBAL_FILTER, SET_FILTERED_ROWS } from "../../actions/types";

export const OrdersTable = ({
  items,
  getLayouts,
  currentLayout,
  savedLayouts,
  saveLayout,
  selectLayout,
  deleteLayout,
  patchLayout,
  editLayout,
  setSelectedRow,
  isCommitted,
  commit,
  undoCommit,
  updateTableCellsAndSync,
  updateTableRow,
  ordersCalculationId,
  currentWorkspaceId,
  currentUser,
  canEditCurrentWorkspace,
  loadingUpdate,
  isActualOrderUpdateInProgress,
  pageIndex,
  pageSize,
  setCurrentPage,
  setPageSize,
  datasetColumns,
  hasBatchData,
  selectedRow,
  orderErrors,
  notificationFilterArray,
  selectedActualOrderCell,
  orderTableContainerWidth,
  draftDynamicField,
  dynamicFieldCrudOperationError,
  dynamicFieldLoadingCrudOperation,
  dynamicFieldNamesTitlesMap,
  dynamicFieldsWithActualOrder,
  singleCalcTableCellResults,
  doLoadMetadata,
  doSaveDynamicField,
  doSingleCalcForTableCell,
  doUpdateDynamicField,
  updateTableCellByRowAndColumn,
  workspaceLevelSettings,
  setNotificationFilter,
}) => {
  const { t, i18n } = useTranslation();
  
  const [currentSingleCalcRowIndex, setCurrentSingleCalcRowIndex] = useState(-1);

  const dispatchState = useDispatch()

  useEffect(() => {
    if (!_.isNil(currentWorkspaceId)) {
      getLayouts(currentWorkspaceId);
    }
  }, [getLayouts, currentWorkspaceId]);

  useEffect(() => {
    doLoadMetadata(currentWorkspaceId);
  }, [currentWorkspaceId, doLoadMetadata]);

  //This function update order of columns
  const updateTableData = useCallback((
    rowIndex,
    columnId,
    value,
    performRequest = false
  ) => {
    if (performRequest) {
      updateTableRow(items[rowIndex], columnId, value);
      updateTableCellsAndSync(
        ordersCalculationId,
        currentWorkspaceId,
        items[rowIndex][itemAccessors.code],
        items[rowIndex][itemAccessors.location],
        columnId === itemAccessors.actualOrder
          ? value
          : items[rowIndex][itemAccessors.actualOrder],
        columnId === itemAccessors.orderVariance
          ? value
          : items[rowIndex][itemAccessors.orderVariance]
      );
      setCurrentUpdateTableRowIndex(rowIndex);
    } else {
      updateTableRow(items[rowIndex], columnId, value);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, updateTableRow, updateTableCellsAndSync, ordersCalculationId, currentWorkspaceId]);

  const checkAndUpdateActualOrderDynamicFields = (rowIndex) => {
    // early return if there are no dynamic fields with a formula that contain actual_order
    const columnIds = Object.keys(dynamicFieldsWithActualOrder);
    if (columnIds.length === 0) {
      return;
    }
    
    if (!items[rowIndex]) {
      return;
    }
    const item = items[rowIndex];

    const rowId = (item.material_location + '_' + item.material_code).replaceAll(/\s+/g, '_');
    setCurrentSingleCalcRowIndex(rowIndex);
    _.forEach(columnIds, columnId => {
      const formula = dynamicFieldsWithActualOrder[columnId];
      const substituteArgValues = [];
      if (formula.indexOf("actual_order") > -1) {
        // only parse for substitution if the formula actually contains an actual_order reference
        const parsed = parseFormulaForCalculation(formula);
        const mappedArgs = parsed.mappedArgs;
        const namedArgs = Object.keys(mappedArgs);
        for (const namedArg of namedArgs) {
          const lcNamedArg = namedArg;
          if (/`?item`?\.?`?actual_order`?/gi.test(lcNamedArg)) {
            let substValue = {};
            substValue[namedArg] = item.actual_order;
            substituteArgValues.push(substValue);
          }
        }
      }

      doSingleCalcForTableCell(currentWorkspaceId, formula, rowId, rowIndex, columnId, substituteArgValues);
    });
  }

  // eslint-disable-next-line
  const itemForCurrentSingleCalcRowIndex = useMemo(() => items[currentSingleCalcRowIndex], [currentSingleCalcRowIndex, items]);

  const updateTableCellForSingleCalc = useCallback((columnId, value, actualOrderValue) => {
    updateTableCellByRowAndColumn(itemForCurrentSingleCalcRowIndex, columnId, value, actualOrderValue);
  }, [updateTableCellByRowAndColumn, itemForCurrentSingleCalcRowIndex]);

  useEffect(() => {
    if (currentSingleCalcRowIndex === -1 || !singleCalcTableCellResults[currentSingleCalcRowIndex]) {
      return;
    }

    const calcResults = singleCalcTableCellResults[currentSingleCalcRowIndex];
    _.forEach(Object.keys(calcResults), columnId => {
      const value = calcResults[columnId];
      if (value.trim().length > 0) {
        updateTableCellForSingleCalc(columnId, value);
      }
    });
  }, [
    currentSingleCalcRowIndex,
    singleCalcTableCellResults,
    updateTableCellByRowAndColumn,
    updateTableCellForSingleCalc
  ]);

  const itemKeysString = items.length > 0 ? Object.keys(items[0]).join(',') : '';

  const columns = useMemo(() => {
    if (items.length === 0) return [];
    const unsortedColumns = mapAccessorsToColumns(
      t,
      Object.keys(items[0]),
      ActualOrderCell,
      VarianceCell,
      datasetColumns,
      dynamicFieldNamesTitlesMap,
      workspaceLevelSettings.decimals_number
    );

    return getSortedColumns(unsortedColumns, hasBatchData);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, itemKeysString, datasetColumns, hasBatchData, dynamicFieldNamesTitlesMap]);

  const memoizedfilterTypes = useMemo(() => {
    return {
      equalsAny,
      multipleOptions,
    };
  }, []);

  const memoizedGlobalFilter = useMemo(() => {
    return (rows, columnIds, filterValue) => {
      return includesPartial(rows, columnIds, [filterValue])
    };
  }, []);

  const [editingLayout, setEditingLayout] = useState(false);
  const [showClearFilterConfirm, setShowClearFilterConfirm] = useState(false);

  const [showCommitConfirm, setShowCommitConfirm] = useState(false);
  const [showNewLayoutModal, setShowNewLayoutModal] = useState(false);
  const [showDeleteLayoutConfirm, setShowDeleteLayoutConfirm] = useState(false);
  const [showOverwriteLayoutConfirm, setShowOverwriteLayoutConfirm] = useState(false);
  const [showSetDefaultLayoutConfirm, setShowSetDefaultLayoutConfirm] = useState(false);
  const [showShareLayoutConfirm, setShowShareLayoutConfirm] = useState(false);
  const [columnsConfig, setColumnsConfig] = useState({})
  const [showDynamicFieldsEditor, setShowDynamicFieldsEditor] = useState(false);
  const [currentUpdateTableRowIndex, setCurrentUpdateTableRowIndex] = useState(-1);
  const [hasActualOrderUpdateInitiated, setHasActualOrderUpdateInitiated] = useState(false);

  useEffect(() => {
    formatDynamicFieldValues(items, singleCalcTableCellResults, currentSingleCalcRowIndex);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSingleCalcRowIndex, singleCalcTableCellResults]);

  // useTable Hook to send the columns and data to build the table
  const tableInstance = useTable(
    {
      columns,
      data: items,
      initialState: {
        ...currentLayout.initialState,
        pageIndex: pageIndex,
        pageSize: pageSize,
      },
      stateReducer: (newState, action) => {
        if (action.type === "changeLayout") {
          const selectedLayout = savedLayouts.find(
            (layout) => layout.id === action.payload
          );
          return {
            ...newState,
            hiddenColumns: selectedLayout && selectedLayout.initialState ? selectedLayout.initialState.hiddenColumns : [],
            columnOrder: selectedLayout && selectedLayout.initialState ? (selectedLayout.initialState.columnOrder || []) : [],
          };
        }
        return newState;
      },
      filterTypes: memoizedfilterTypes,
      globalFilter: memoizedGlobalFilter,
      updateTableData,
      decimalsNumber: _.get(workspaceLevelSettings, "decimals_number", 2),
      // Some unexpected updates of table can be controlled with use table properties. Check this answer:
      // https://stackoverflow.com/questions/66656018/prevent-re-sort-on-data-change-or-while-row-is-being-edited 
      autoResetFilters: false,
      autoResetSortBy: false,
    },
    useColumnOrder,
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const { allColumns, dispatch, setHiddenColumns, setColumnOrder } = tableInstance;

  useLayoutEffect(() => {
    if (currentLayout && currentLayout.initialState && currentLayout.initialState.columnOrder && currentLayout.initialState.columnOrder.length > 0) {
      setColumnOrder(currentLayout.initialState.columnOrder);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLayout.id]);

  // To be called when the layout is edited, i.e. a column is toggled
  const onColumnToggle = () => {
    setEditingLayout(true);
  };

  // Called when user drags a column header to a new position
  const onColumnReorder = useCallback((activeId, overId) => {
    const allColumnIds = tableInstance.allColumns.map((c) => c.id);
    const savedOrder = tableInstance.state.columnOrder;
    const currentOrder = savedOrder.length > 0
      ? [...savedOrder, ...allColumnIds.filter((id) => !savedOrder.includes(id))]
      : allColumnIds;

    const oldIndex = currentOrder.indexOf(activeId);
    const newIndex = currentOrder.indexOf(overId);

    if (oldIndex === -1 || newIndex === -1) return;

    const STICKY_COUNT = 3;
    if (oldIndex < STICKY_COUNT || newIndex < STICKY_COUNT) return;

    setColumnOrder(arrayMove(currentOrder, oldIndex, newIndex));
    setEditingLayout(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setColumnOrder]);

  const onLayoutSelected = (id) => {
    setEditingLayout(false);
    dispatch({ type: "changeLayout", payload: id });
    selectLayout(id);
  };

  const onSaveLayout = (name) => {
    saveLayout(currentWorkspaceId, {
      name: name,
      initialState: tableInstance.state,
    });
    setEditingLayout(false);
  };

  const isDuplicateName = (name) => {
    return _.includes(
      savedLayouts.map((layout) => layout.name),
      name
    );
  };

  const shouldDisableCommit = () => {
    return isCommitted || loadingUpdate;
  };

  const handleClickSaveDynamicField = () => {
    if (draftDynamicField.dynamic_field_id > 0) {
      doUpdateDynamicField(currentWorkspaceId);
      return;
    }
    doSaveDynamicField(currentWorkspaceId);
  }

  useEffect(() => {
    if (!dynamicFieldLoadingCrudOperation) {
      if (dynamicFieldCrudOperationError) {
        // early return to keep the modal open if an error occurred
        return;
      }

      setShowDynamicFieldsEditor(false);
    }
  }, [dynamicFieldCrudOperationError, dynamicFieldLoadingCrudOperation, setShowDynamicFieldsEditor]);

  useEffect(() => {
    dispatchState({ type: SET_FILTERED_ROWS, payload: tableInstance.preSortedRows.map(row => row.original) });
  }, [tableInstance.preSortedRows, dispatchState]);

  const handleSetFilter = useCallback((item, values) => {
    dispatchState({type: GET_FILTERS, payload: {[item]: values}});
    tableInstance.setFilter(item, values);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClearAllFilters = useCallback(() => {
    tableInstance.state.filters
      .filter(f => f.id !== itemAccessors.suggestedOrder)
      .forEach(f => tableInstance.setFilter(f.id, undefined));
    setNotificationFilter('');
    setShowClearFilterConfirm(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableInstance.state.filters, setNotificationFilter]);

  const memoizedPrepareRow = useMemoCompare(
    {
      prepareRow: tableInstance.prepareRow,
      tableState: tableInstance.state,
      ordersCalculationId: ordersCalculationId,
      hasBatchData: hasBatchData,
      language: i18n.language,
    },
    (prev) => {
      return (
        prev &&
        _.isEqual(prev.tableState, tableInstance.state) &&
        _.isEqual(prev.ordersCalculationId, ordersCalculationId) &&
        _.isEqual(prev.hasBatchData, hasBatchData) &&
        prev.language === i18n.language
      );
    }
  );

  const memoizedTable = useMemo(
    () => { 
      return (
        <Table
          setFilter={handleSetFilter}
          getTableProps={tableInstance.getTableProps}
          getTableBodyProps={tableInstance.getTableBodyProps}
          headerGroups={tableInstance.headerGroups}
          filterRows={tableInstance.filteredRows}
          rows={tableInstance.page}
          allRows={tableInstance.rows}
          prepareRow={memoizedPrepareRow.prepareRow}
          ordersCalculationId={memoizedPrepareRow.ordersCalculationId}
          onSelectRow={setSelectedRow}
          editable={canEditCurrentWorkspace && !isCommitted}
          tableState={tableInstance.state}
          selectedRow={selectedRow}
          onColumnReorder={onColumnReorder}
          rulesByItem={!_.isNil(workspaceLevelSettings) 
            && workspaceLevelSettings.buffer_rules_by_item}
          decimalsNumber={_.get(workspaceLevelSettings, "decimals_number", 2)}
        />
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // Ignoring exhaustive deps warnings because changes in tableInstance
      // are already accounted for in memoizedPrepareRow
      ordersCalculationId,
      items,
      memoizedPrepareRow,
      setSelectedRow,
      selectedRow,
      isCommitted,
      canEditCurrentWorkspace,
      t,
      onColumnReorder,
    ]
  );

  //Notification panel filter
  //watch changes of notificationFilterArray in OnHandStockSummary component
  // tableInstance intentionally omitted from deps — it changes every render and the filter should only re-apply when notificationFilterArray changes
  useEffect(()=>{
    if(Array.isArray(notificationFilterArray)){
      tableInstance.setFilter("location_code_concatenation", notificationFilterArray)
      return
    }
    tableInstance.setAllFilters([])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationFilterArray])

  // Horizontal scroll when user select row to show order cell in screen
  useEffect(() => {
    if(selectedActualOrderCell){
      document.getElementById("container-data-table").scrollLeft += orderTableContainerWidth;
    }
  }, [ selectedActualOrderCell, orderTableContainerWidth ])

  
  useEffect(() => {
    if (isActualOrderUpdateInProgress) {
      setHasActualOrderUpdateInitiated(true);
    }
    if (isActualOrderUpdateInProgress || currentUpdateTableRowIndex === -1) {
      return;
    }
    if (!hasActualOrderUpdateInitiated) {
      return;
    }
    checkAndUpdateActualOrderDynamicFields(currentUpdateTableRowIndex);
    setHasActualOrderUpdateInitiated(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isActualOrderUpdateInProgress,
    currentUpdateTableRowIndex,
    hasActualOrderUpdateInitiated,
    setHasActualOrderUpdateInitiated
  ]);


  return (
    <div>     
      <div className="orders-table">  

        {/* Modal to give a name to configuration */}
        {showNewLayoutModal && (
          <CustomLayoutModal
            onSubmit={onSaveLayout}
            onCancel={() => setShowNewLayoutModal(false)}
            checkDuplicate={isDuplicateName}
          />
        )}
        
        {/* Confirm messages when you ara making changes */}
        <Confirm
          open={showDeleteLayoutConfirm}
          content={t("ordersTable.layouts.deleteConfirmMessage", {
            layout: currentLayout.name,
          })}
          cancelButton={t("cancel")}
          confirmButton={t("confirm")}
          onCancel={() => setShowDeleteLayoutConfirm(false)}
          onConfirm={() => {
            deleteLayout(currentWorkspaceId, currentLayout.id);
            setShowDeleteLayoutConfirm(false);
          }}
        />
        <Confirm
          open={showOverwriteLayoutConfirm}
          content={t("ordersTable.layouts.overwriteConfirmMessage", {
            layout: currentLayout.name,
          })}
          cancelButton={t("cancel")}
          confirmButton={t("confirm")}
          onCancel={() => setShowOverwriteLayoutConfirm(false)}
          onConfirm={() => {
            // Here have to change setHiddenColumns because table instance doesn't catch the last change when click at change button
            setHiddenColumns(columnsConfig.hiddenColumns);
            setColumnOrder(columnsConfig.columnOrder ?? []);
            editLayout(currentWorkspaceId, columnsConfig, currentLayout);
            setShowOverwriteLayoutConfirm(false);
          }}
        />
        <Confirm
          open={showSetDefaultLayoutConfirm}
          content={t(
            currentLayout.isDefault ? "ordersTable.layouts.unsetDefaultConfirmMessage" : "ordersTable.layouts.setDefaultConfirmMessage", {
            layout: currentLayout.name,
          })}
          cancelButton={t("cancel")}
          confirmButton={t("confirm")}
          onCancel={() => setShowSetDefaultLayoutConfirm(false)}
          onConfirm={() => {
            patchLayout(currentWorkspaceId, currentLayout.id, "is_default");
            setShowSetDefaultLayoutConfirm(false)
          }}
        />
        <Confirm
          open={showShareLayoutConfirm}
          content={t(
            currentLayout.isShared ? "ordersTable.layouts.unsetSharedConfirmMessage" : "ordersTable.layouts.setSharedConfirmMessage", {
            layout: currentLayout.name,
          })}
          cancelButton={t("cancel")}
          confirmButton={t("confirm")}
          onCancel={() => setShowShareLayoutConfirm(false)}
          onConfirm={() => {
            patchLayout(currentWorkspaceId, currentLayout.id, "is_shared");
            setShowShareLayoutConfirm(false)
          }}
        />


        <Grid stackable>
          <Grid.Row columns={2}>
            <Grid.Column
              stretched
              width={11}
              verticalAlign="middle"
              >
              <Grid.Row className="order-table-interactive-buttons">
                  <DataExportButton 
                    tableInstance={tableInstance}
                    dynamicFieldNamesTitlesMap={dynamicFieldNamesTitlesMap} />

                  <SuggestedOrderFilter
                    column={getSuggestedOrderColumn(allColumns)}
                    isFilterActive={tableInstance.state.filters.some(f => f.id !== itemAccessors.suggestedOrder)}
                    activeFilters={tableInstance.state.filters.filter(f => f.id !== itemAccessors.suggestedOrder)}
                    allColumns={allColumns}
                    onClearColumnFilter={(id) => handleSetFilter(id, undefined)}/>
                  {tableInstance.state.filters.some(f => f.id !== itemAccessors.suggestedOrder) && (
                    <Button
                      icon="times circle outline"
                      className="order-column-button filter-clear-button"
                      onClick={() => setShowClearFilterConfirm(true)}
                    />
                  )}

                  <OrderColumns
                        currentLayout={currentLayout}
                        savedLayouts={savedLayouts}
                        allColumns={allColumns}
                        onLayoutSelected={onLayoutSelected}
                        setShowOverwriteLayoutConfirm={setShowOverwriteLayoutConfirm}
                        tableInstance={tableInstance}
                        setColumnsConfig={setColumnsConfig}
                        setShowDeleteLayoutConfirm={setShowDeleteLayoutConfirm}
                        setShowSetDefaultLayoutConfirm={setShowSetDefaultLayoutConfirm}
                        setShowShareLayoutConfirm={setShowShareLayoutConfirm}
                        currentUserId={currentUser ? currentUser.id : null}
                        onColumnToggle={onColumnToggle}
                        setShowNewLayoutModal={setShowNewLayoutModal}
                        editingLayout={editingLayout}
                    />

                <Grid.Column
                  width={5}
                  className="search-bar-input-column"
                  >
                  <SearchBar
                    setFilter={ (value) => { dispatchState({type: GET_GLOBAL_FILTER, payload: value }); tableInstance.setGlobalFilter(value)}}
                    rawColumns={columns}
                    headerGroups={tableInstance.headerGroups}
                  />
                </Grid.Column>

                <Grid.Column
                  floated="left">
                  <Modal
                    open={showDynamicFieldsEditor}
                    trigger={
                      <DynamicFieldsButton
                        onClick={() => setShowDynamicFieldsEditor(true)}
                      />
                    }
                    onCancel={() => setShowDynamicFieldsEditor(false)}
                    content={<DynamicFieldsModal />}
                    actions={[
                      {
                        primary: true,
                        key: "save",
                        content: t("save"),
                        loading: dynamicFieldLoadingCrudOperation,
                        disabled: dynamicFieldLoadingCrudOperation,
                        onClick: () => {
                          handleClickSaveDynamicField();
                        }
                      },
                      {
                        key: "cancel",
                        content: t("close"),
                        disabled: dynamicFieldLoadingCrudOperation,
                        onClick: () => setShowDynamicFieldsEditor(false),
                      },
                    ]} />
                </Grid.Column>
              </Grid.Row>
            </Grid.Column>

            <GridColumn
              verticalAlign="middle"
              width={5}
            >
              <Grid>
                <Grid.Row>
                  <Grid.Column className="progress-indicator-column" width={6}>
                    <ProgressIndicator />
                  </Grid.Column>
                  <Grid.Column width={10}>
                    <span className="commit-undo-bar">
                      {isCommitted ? (
                        <Modal
                          open={showCommitConfirm}
                          trigger={
                            <Button
                              secondary
                              disabled={!canEditCurrentWorkspace}
                              onClick={() => setShowCommitConfirm(true)}
                            >
                              {t("ordersTable.commitUndoButton")}
                            </Button>
                          }
                          onCancel={() => setShowCommitConfirm(false)}
                          content={
                            <Modal.Content>
                              <Header>{t("ordersTable.undoConfirmationMessage")}</Header>
                              <Message warning>
                                {t("ordersTable.pendingChangesWarning")}
                              </Message>
                            </Modal.Content>
                          }
                          actions={[
                            {
                              key: "cancel",
                              content: t("cancel"),
                              onClick: () => setShowCommitConfirm(false),
                            },
                            {
                              key: "confirm",
                              content: t("confirm"),
                              primary: true,
                              onClick: () => {
                                if (!canEditCurrentWorkspace) {
                                  return;
                                }
                                undoCommit(ordersCalculationId, currentWorkspaceId);
                                setShowCommitConfirm(false);
                              },
                            },
                          ]}
                        ></Modal>
                      ) : (
                          <Confirm
                            open={showCommitConfirm}
                            trigger={
                              <div >
                                  <Button
                                    positive
                                    disabled={!canEditCurrentWorkspace || shouldDisableCommit()}
                                    onClick={() => {
                                      if (!canEditCurrentWorkspace) {
                                        return;
                                      }
                                      setShowCommitConfirm(true);
                                    }}
                                  >
                                    {t("ordersTable.commitButton")}
                                  </Button>

                              </div>
                            }
                            onConfirm={() => {
                              if (!canEditCurrentWorkspace) {
                                return;
                              }
                              commit(
                                ordersCalculationId,
                                currentWorkspaceId,
                                mapItemsToRecordsObject(items)
                              );
                              setShowCommitConfirm(false);
                            }}
                            onCancel={() => setShowCommitConfirm(false)}
                            content={t("ordersTable.commitConfirmationMessage")}
                            cancelButton={t("cancel")}
                            confirmButton={t("confirm")}
                          ></Confirm>
                        )}
                    </span>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </GridColumn>
          </Grid.Row>
          {
            !_.isEmpty(orderErrors) 
                        &&  <Grid.Row className="row-errors-order">
                              <Grid.Column
                                floated='right'
                                with={8}
                                className="errors-order"
                                >
                                  <OrdersErrorDropdown
                                    items={items}
                                    />
                              </Grid.Column>
                            </Grid.Row>
            }
        </Grid>



        {!_.isNil(ordersCalculationId) && memoizedTable}

        {items && items.length > pageSize && (
          <PageSelector
            canPreviousPage={tableInstance.canPreviousPage}
            canNextPage={tableInstance.canNextPage}
            pageOptions={tableInstance.pageOptions}
            pageCount={tableInstance.pageCount}
            gotoPage={(value) => {
              setCurrentPage(value);
              tableInstance.gotoPage(value);
            }}
            nextPage={() => {
              setCurrentPage(pageIndex + 1);
              tableInstance.nextPage();
            }}
            previousPage={() => {
              setCurrentPage(pageIndex - 1);
              tableInstance.previousPage();
            }}
            setPageSize={(value) => {
              setPageSize(Number(value));
              tableInstance.setPageSize(Number(value));
            }}
          />
        )}
        <Confirm
          open={showClearFilterConfirm}
          onConfirm={onClearAllFilters}
          onCancel={() => setShowClearFilterConfirm(false)}
          content={t("ordersTable.filters.clearFilterConfirmation")}
          cancelButton={t("cancel")}
          confirmButton={t("confirm")}
        />
      </div>
    </div>
  );
};

OrdersTable.propTypes = {
  items: PropTypes.array.isRequired,
  getLayouts: PropTypes.func,
  currentLayout: PropTypes.object,
  savedLayouts: PropTypes.arrayOf(PropTypes.object),
  saveLayout: PropTypes.func,
  selectLayout: PropTypes.func,
  deleteLayout: PropTypes.func,
  patchLayout: PropTypes.func,
  editLayout: PropTypes.func,
  setSelectedRow: PropTypes.func,
  isCommitted: PropTypes.bool,
  commit: PropTypes.func,
  undoCommit: PropTypes.func,
  updateTableCellsAndSync: PropTypes.func,
  updateTableRow: PropTypes.func,
  ordersCalculationId: PropTypes.number,
  currentWorkspaceId: PropTypes.number,
  currentUser: PropTypes.object,
  canEditCurrentWorkspace: PropTypes.bool,
  loadingUpdate: PropTypes.bool,
  isActualOrderUpdateInProgress: PropTypes.bool,
  pageIndex: PropTypes.number,
  pageSize: PropTypes.number,
  setCurrentPage: PropTypes.func,
  setPageSize: PropTypes.func,
  datasetColumns: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object
  ]),
  hasBatchData: PropTypes.bool,
  selectedRow: PropTypes.object,
  orderErrors: PropTypes.array,
  notificationFilterArray: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.string,
  ]),
  selectedActualOrderCell: PropTypes.bool,
  orderTableContainerWidth: PropTypes.number,
  dynamicFieldCrudOperationError: PropTypes.bool,
  dynamicFieldLoadingCrudOperation: PropTypes.bool,
  dynamicFieldNamesTitlesMap: PropTypes.object,
  dynamicFieldsWithActualOrder: PropTypes.object,
  draftDynamicField: PropTypes.object,
  singleCalcTableCellResults: PropTypes.object,
  doLoadMetadata: PropTypes.func,
  doSaveDynamicField: PropTypes.func,
  doSingleCalcForTableCell: PropTypes.func,
  doUpdateDynamicField: PropTypes.func,
  updateTableCellByRowAndColumn: PropTypes.func,
  setNotificationFilter: PropTypes.func,
  workspaceLevelSettings: PropTypes.object,
};

OrdersTable.defaultProps = {
  getLayouts: _.noop,
  currentLayout: {},
  savedLayouts: [],
  saveLayout: _.noop,
  selectLayout: _.noop,
  deleteLayout: _.noop,
  patchLayout: _.noop,
  editLayout: _.noop,
  setSelectedRow: _.noop,
  isCommitted: false,
  commit: _.noop,
  undoCommit: _.noop,
  updateTableCellsAndSync: _.noop,
  updateTableRow: _.noop,
  ordersCalculationId: 0,
  currentWorkspaceId: 0,
  loadingUpdate: false,
  isActualOrderUpdateInProgress: false,
  pageIndex: 0,
  pageSize: 10,
  setCurrentPage: _.noop,
  setPageSize: _.noop,
  hasBatchData: false,
  canEditCurrentWorkspace: false,
  orderErrors: [],
  draftDynamicField: {},
  dynamicFieldCrudOperationError: false,
  dynamicFieldLoadingCrudOperation: false,
  dynamicFieldNamesTitlesMap: {},
  dynamicFieldsWithActualOrder: {},
  singleCalcTableCellResults: {},
  doLoadMetadata: _.noop,
  doSaveDynamicField: _.noop,
  doSingleCalcForTableCell: _.noop,
  doUpdateDynamicField: _.noop,
  updateTableCellByRowAndColumn: _.noop,
};

const mapStateToProps = (state) => {
  return {
    currentLayout: state.ordersTable.currentLayout,
    savedLayouts: state.ordersTable.savedLayouts,
    isCommitted: state.ordersTable.isCommitted,
    ordersCalculationId: state.ordersTable.ordersCalculationId,
    currentWorkspaceId: state.workspace.currentWorkspace,
    currentUser: state.auth.user,
    canEditCurrentWorkspace:
      state.auth.isConnected &&
      resolveEditPermission(state.auth.user, getSafeSelectedWorkspace(state)),
    items: state.items.items,
    loadingUpdate: state.ordersTable.loadingUpdate,
    isActualOrderUpdateInProgress: state.items.isActualOrderUpdateInProgress,
    pageIndex: state.items.pageIndex,
    pageSize: state.items.pageSize,
    datasetColumns: state.dataset.columns,
    hasBatchData: !_.isNil(state.items.batchData),
    selectedRow: state.ordersTable.selectedRow,
    orderErrors: state.ordersTable.orderErrors,
    notificationFilterArray: state.ordersTable.notificationFilterArray,
    selectedActualOrderCell: state.ordersTable.selectedActualOrderCell,
    orderTableContainerWidth: state.ordersTable.orderTableContainerWidth,
    draftDynamicField: state.dynamicFields.draftDynamicField,
    dynamicFieldCrudOperationError: state.dynamicFields.crudOperationError,
    dynamicFieldLoadingCrudOperation: state.dynamicFields.loadingCrudOperation,
    dynamicFieldNamesTitlesMap: state.dynamicFields.dynamicFieldNamesTitlesMap,
    dynamicFieldsWithActualOrder: state.dynamicFields.dynamicFieldsWithActualOrder,
    singleCalcTableCellResults: state.dynamicFields.singleCalcTableCellResults,
    doLoadMetadata: state.dynamicFields.doLoadMetadata,
    doSaveDynamicField: state.dynamicFields.doSaveDynamicField,
    doSingleCalcForTableCell: state.dynamicFields.doSingleCalcForTableCell,
    doUpdateDynamicField: state.dynamicFields.doUpdateDynamicField,
    updateTableCellByRowAndColumn: state.ordersTable.updateTableCellByRowAndColumn,
    workspaceLevelSettings: state.adminConsole.workspaceLevelSettings,
  };
};

export default connect(mapStateToProps, {
  getLayouts,
  saveLayout,
  selectLayout,
  deleteLayout,
  patchLayout,
  editLayout,
  setSelectedRow,
  commit,
  undoCommit,
  updateTableCellsAndSync,
  updateTableRow,
  setCurrentPage,
  setPageSize,
  doLoadMetadata,
  doSaveDynamicField,
  doSingleCalcForTableCell,
  doUpdateDynamicField,
  updateTableCellByRowAndColumn,
  setNotificationFilter,
})(OrdersTable);
